import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Icon } from '@mui/material';
import { Typography, Card } from '@totalsoft/rocket-ui';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import GenericFilterComposer, { defaultTemplate, setInitialData } from '@ts-private/generic-filter-composer';
import useGenericFilterData, { GenericFilterContext } from 'features/common/hooks/useGenericFilterData';
import { UPDATE_FINANCIAL_PRODUCT_RULE_FILTERS } from './mutations';
import { FINANCIAL_PRODUCT_RULE_FILTERS_UPDATED } from './subscriptions';
import FilterList from '@mui/icons-material/FilterList';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';

const FinancialProductRuleFiltersContainer = ({ ruleId, phrase, conditions, canWrite }) => {
  const { t } = useTranslation();
  const addToast = useToast();

  const isNewRule = useMemo(() => !ruleId, [ruleId]);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(defaultTemplate);

  // #region Data fetching

  const { parameters, operators, loadValues, loadPagedValues } = useGenericFilterData({
    context: GenericFilterContext.FinancialProduct
  });

  // #endregion

  // #region Side effects

  useEffect(() => {
    const sanitizedConditions = (conditions || []).map(
      ({ filterId, paramId, operatorId, orderNo, filterValue, filterValueId, filterValues }) => ({
        id: filterId,
        paramId,
        operatorId,
        orderNo,
        value: filterValue,
        valueId: filterValueId,
        values: (filterValues || []).map(({ filterValue, filterValueId }) => ({ value: filterValue, valueId: filterValueId }))
      })
    );
    const data = {
      withTemplate: false,
      templateId: null,
      disabled: !canWrite,
      template: {
        phrase: phrase || null,
        conditions: sanitizedConditions
      }
    };
    resetLens(data);
    setInitialData(data);
  }, [phrase, conditions, canWrite, resetLens]);

  // #endregion

  // #region Save

  const [mutateFilters] = useMutation(UPDATE_FINANCIAL_PRODUCT_RULE_FILTERS, {
    onError: _ => {
      (lens.disabled |> set)(false);
    }
  });

  const handleSave = data => {
    (lens.disabled |> set)(true);

    const { template } = data;

    const input = { ruleId, ...template };

    mutateFilters({
      variables: { input },
      errorHandlingParams: {}
    });
  };

  useSubscription(FINANCIAL_PRODUCT_RULE_FILTERS_UPDATED, {
    skip: !ruleId,
    variables: { ruleId },
    onData: ({ data: { data }, client }) => {
      const currentData = lens |> get;
      const newData = {
        ...currentData,
        template: { ...currentData.template, conditions: currentData.template.conditions.map(x => ({ ...x, editing: false })) },
        disabled: false
      };
      resetLens(newData);
      setInitialData(newData);
      addToast(t('FinancialProduct.Details.Rules.Filters.SaveSucceeded'), 'success');
      client.cache.evict({
        id: `FinancialProductRuleEditItem:{"financialProductRuleId":${data.financialProductRuleFiltersUpdated.ruleId}}`
      });
      client.cache.gc();
    }
  });

  // #endregion

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  return (
    <Card icon={FilterList} iconColor='primary' title={t('FinancialProduct.Details.Rules.Filters.Title')}>
      <Grid container flexDirection='column' gap={2} p={2}>
        {isNewRule && (
          <Box display='flex' gap={1} alignItems='center' justifyContent='center' flex>
            <Icon fontSize='small' color='primary'>
              error
            </Icon>
            <Typography color='textPrimary'>{t('FinancialProduct.Details.PleaseCompleteGeneralData')}</Typography>
          </Box>
        )}
        {!isNewRule && (
          <GenericFilterComposer
            lens={lens}
            dirtyInfo={dirtyInfo}
            resetLens={resetLens}
            parameters={parameters}
            operators={operators}
            loadValues={loadValues}
            loadPagedValues={loadPagedValues}
            onSave={handleSave}
            conditionsViewMode='table'
            withTemplateGeneralData
          />
        )}
      </Grid>
    </Card>
  );
};

FinancialProductRuleFiltersContainer.propTypes = {
  ruleId: PropTypes.number,
  phrase: PropTypes.string,
  conditions: PropTypes.array,
  canWrite: PropTypes.bool
};

export default FinancialProductRuleFiltersContainer;
